import React, { useEffect, useState } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import ActionDropdownItem from "../../components/Common/PageTitle/ActionDropdownItem";
import ActivityLoader from "../../components/Common/ActivityLoader";
import Notification from "../../components/Common/Notification";
import functions from "../../util/functions";
import Pagination from "../../components/Common/Pagination";
import { useTheme } from "../../DataStore/ThemeContext";
import { useOnlineSchoolFees } from "../../DataStore/OnlineSchoolFeesContext";
import ConfirmationDialog from "../../components/Common/ConfirmationDialog";
import ajaxSchool from "../../util/remote/ajaxSchool";
import AlertDateSelector from "../../components/Report/AlertDateSelector";
import OnlineFeesFilterBar from "../../components/SchoolFees/OnlineFeesFilterBar";

const OnlineSchoolPaymentPage = () => {
  const { paymentList, getTransactions, setCurrentPage, metaData, refresh, setSelectedIds, selectedIds } = useOnlineSchoolFees();
  const [selected, setSelected] = useState(null);
  const { setNotify, openSettings } = useTheme();
  const [showDelete, toggleDelete] = useState(false);
  const [showDateRange, setShowDateRange] = useState(false);
  const [showReconciled, toggleReconciled] = useState(false);
  const [showUnreconciled, toggleUnreconciled] = useState(false);
  const [showProcessConfirm, toggleProcessConfirm] = useState(false);
  const [showReconcile, toggleReconcile] = useState(false);
  const [processType, setProcessType] = useState('all'); // "all" or "selection"

  useEffect(() => {
    getTransactions();
  }, []);

  // Toggle selection for a transaction (same as TransactionList pattern)
  const toggleSelection = (id) => {
    // guard against non-array (prevents the map/includes crashes)
    const safeSelected = Array.isArray(selectedIds) ? selectedIds : [];

    setSelectedIds(
      safeSelected.includes(id)
        ? safeSelected.filter((selectedId) => selectedId !== id)
        : [...safeSelected, id]
    );
  };

  // Check if a transaction is selected
  const isSelected = (id) => {
    const safeSelected = Array.isArray(selectedIds) ? selectedIds : [];
    return safeSelected.includes(id);
  };

  const handleProcess = (type) => {
    if(type === "selection" && selectedIds.length === 0){
        setNotify(<Notification type="info" message="No transaction selected. Please make selection then try again."/>);
        return;
    }
    setProcessType(type);
    toggleProcessConfirm(true);
  }

  const handleDelete = ()=>{
    if(selectedIds.length === 0){
        setNotify(<Notification type="info" message="No transaction selected. Please make selection then try again."/>);
        return;
    }
    toggleDelete(true);
  }

  const handleDeleteConfirmed = async()=>{
    setNotify(<Notification message="Deleting selected transactions. Please wait..." loader={true}/>);
    const server_response = await ajaxSchool.deleteOnlinePayments({payment_ids: selectedIds});
    if(server_response.status === "OK"){
      setNotify(<Notification type="success" message={server_response.message} closable={false}/>);
      clearSelection();
      refresh();
    }else{
        setNotify(<Notification type="danger" message={server_response.message} closable={false}/>);
    }
  }

  const handleProcessPayments = async()=>{
    setNotify(<Notification message="Processing online payments. Please wait..." loader={true}/>);
    const server_response = await ajaxSchool.processOnlinePayments();
    if(server_response.status === "OK"){
      setNotify(<Notification type="success" message={server_response.message} closable={false}/>);
      refresh();
    }else{
        setNotify(<Notification type="danger" message={server_response.message} closable={false}/>);
    }
  }

  const checkForPayments = async(dateRange)=>{
    setNotify(<Notification message="Checking for online payments. Please wait..." loader={true}/>);
    const server_response = await ajaxSchool.checkOnlinePayments(dateRange.opening_date, dateRange.closing_date);
    if(server_response.status === "OK"){
        setNotify(<Notification type="success" message={server_response.message} closable={false}/>);
        refresh();
    }else{
        setNotify(<Notification type="danger" message={server_response.message} closable={false}/>);
    }
  }

  const handleFilter = () => {
    refresh();
  }

  const clearSelection = () => {
    setSelected(null);
    setSelectedIds([]);
  };

  const handleChangeSettlementBank = () => {
    if(selectedIds.length === 0){
        setNotify(<Notification type="info" message="No transaction selected. Please make selection then try again."/>);
        return;
    }
    openSettings("Change Settlement Bank", "ChangeSettlementBank", {selection:selectedIds, refresh: refresh}, "Update Bank");
  }

  const handleReconciledConfirmed = async()=>{
    setNotify(<Notification message="Marking selected transactions as reconciled. Please wait..." loader={true}/>);
    const server_response = await ajaxSchool.markReconciled({ids: selectedIds});
    if(server_response.status === "OK"){
      setNotify(<Notification type="success" message={server_response.message} closable={false}/>);
      refresh();
    }else{
        setNotify(<Notification type="danger" message={server_response.message} closable={false}/>);
    }
  }

  const handleUnreconciledConfirmed = async()=>{
    setNotify(<Notification message="Marking selected transactions as unreconciled. Please wait..." loader={true}/>);
    const server_response = await ajaxSchool.markUnreconciled({ids: selectedIds});
    if(server_response.status === "OK"){
      setNotify(<Notification type="success" message={server_response.message} closable={false}/>);
      refresh();
    }else{
        setNotify(<Notification type="danger" message={server_response.message} closable={false}/>);
    }
  }

  const confirmProcessSelection = async()=>{
    toggleProcessConfirm(false);
    setNotify(<Notification message="Processing selected online payments. Please wait..." loader={true}/>);
    const server_response = await ajaxSchool.processOnlinePayments(processType === "selection" ? {ids: selectedIds} : {});
    if(server_response.status === "OK"){
      setNotify(<Notification type="success" message={server_response.message} closable={false}/>);
      refresh();
    }else{
        setNotify(<Notification type="danger" message={server_response.message} closable={false}/>);
    }
  } 

  const confirmReconcileSelection = async()=>{
    toggleReconcile(false);
    setNotify(<Notification message="Reconciling selected online payments. Please wait..." loader={true}/>);
    const server_response = await ajaxSchool.reconcilePayments({ids: selectedIds});
    if(server_response.status === "OK"){
      setNotify(<Notification type="success" message={server_response.message} closable={false}/>);
      refresh();
    }else{
        setNotify(<Notification type="danger" message={server_response.message} closable={false}/>);
    }
  }

  const PageActions = () => (
    <>
      <ActionDropdownItem iconClass="fa fa-globe" label="Check for Payments..." onClick={()=>setShowDateRange(true)}/>
      <ActionDropdownItem iconClass="fa fa-circle-check" label="Process Payments..." onClick={()=>handleProcess("all")}/>
      <ActionDropdownItem iconClass="fa fa-circle-check" label="Process Selection" onClick={()=>handleProcess("selection")} disabled={selectedIds.length === 0}/>
      <ActionDropdownItem iconClass="fa fa-exchange-alt" label="Reconcile Selection" onClick={()=>toggleReconcile(true)} disabled={selectedIds.length === 0}/>
      <ActionDropdownItem iconClass="fa fa-trash-alt text-danger" label="Delete Selection" onClick={handleDelete} disabled={selectedIds.length === 0}/>
      <ActionDropdownItem iconClass="fa fa-check-circle" label="Mark Reconciled" onClick={()=>toggleReconciled(true)} disabled={selectedIds.length === 0}/>
      <ActionDropdownItem iconClass="fa fa-xmark" label="Clear Selection" onClick={clearSelection} disabled={selectedIds.length === 0}/>
      <ActionDropdownItem iconClass="fa fa-university" label="Change Stl. Bank..." onClick={handleChangeSettlementBank} disabled={selectedIds.length === 0}/>
      <ActionDropdownItem iconClass="fa fa-times-circle" label="Mark Unreconciled" onClick={()=>toggleUnreconciled(true)} disabled={selectedIds.length === 0}/>
    
      <ActionDropdownItem
        iconClass="fa fa-sync"
        label="Refresh"
        onClick={refresh}
      />
    </>
  );

  return (
    <>
    <PageContainer>
      <MainContent>
        <PageTitle
          title="Online Fees Payments"
          description="Manage and effect your online school fees payments"
          iconClass="fab fa-amazon-pay"
          PageActions={PageActions}
          sticky={true}
          Pagination={
            metaData ? (
              <Pagination
                metaData={metaData}
                onPageChange={(page) => setCurrentPage(page)}
              />
            ) : null
          }
        />

        <OnlineFeesFilterBar filter={handleFilter} />
         <div className="card mb-3">
            <div className="card-body">
              {Array.isArray(paymentList) && (
                <div className="table-responsive">
                  <table className="table table-hover table-striped table-bordered">
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Select all records on current page
                                setSelectedIds(
                                  Array.isArray(paymentList) ? paymentList.map((t) => t.id) : []
                                );
                              } else {
                                setSelectedIds([]);
                              }
                            }}
                            checked={
                              Array.isArray(paymentList) &&
                              paymentList.length > 0 &&
                              Array.isArray(selectedIds) &&
                              selectedIds.length === paymentList.length
                            }
                          />
                        </th>
                        <th className="nowrap">Transaction Date</th>
                        <th>Gateway</th>
                        <th>Student Name</th>
                        <th>Class</th>
                        <th>Reg No</th>
                        <th>Pay. Code</th>
                        <th>Amount</th>
                        <th className="nowrap">Trans Ref</th>
                        <th>GW.Status</th>
                        <th>Comment</th>
                        <th>CLR. Status</th>
                        <th>Bank</th>
                      </tr>
                    </thead>

                    <tbody>
                      {paymentList.map((transaction) => {
                        return (
                         <tr
                            key={transaction.id}
                            className={`link ${isSelected(transaction.id) ? "selected-row" : ""}`}
                            onClick={() => setSelected(transaction)}
                            title={transaction.reconciled === 1 ? `Reconciled: ${transaction.reconciliation_comment? transaction.reconciliation_comment : "Completed successfully!"}` : transaction.reconciled === 2 ? `Failed to reconcile: ${transaction.reconciliation_comment}` : "Pending reconciliation"}
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={isSelected(transaction.id)}
                                onChange={() => toggleSelection(transaction.id)}
                              />
                            </td>

                            <td className="nowrap">
                              {functions.formatDate(transaction.transaction_date)}
                            </td>
                            <td>{transaction.collector_code}</td>
                            <td>{transaction.student_name}</td>
                            <td>{transaction.class}</td>
                            <td>{transaction.reg_no}</td>
                            <td>{transaction.payment_code}</td>
                            <td>{functions.numberFormat(transaction.amount)}</td>
                            <td>{transaction.transaction_ref}</td>
                            <td>
                              <span
                                className={`badge ${
                                  transaction.status === "success"
                                    ? "badge-success"
                                    : "badge-danger"
                                }`}
                              >
                                {transaction.status}
                              </span>
                            </td>
                            <td>{transaction.comment}</td>
                            <td>
                              <span
                                className={`badge ${
                                  transaction.processed * 1 === 1
                                    ? "badge-success"
                                    : "badge-warning"
                                }`}
                              >
                                {transaction.processed * 1 === 1 ? "Processed" : "Pending"}
                              </span>
                            </td>
                            <td className="nowrap">
                              {transaction.settlement_bank} &nbsp;
                              {transaction.reconciled * 1 === 1 && <i className="fa fa-check-circle text-success"/>}
                              {transaction.reconciled * 1 === 2 && <i className="fa fa-times-circle text-danger"/>}
                              </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {paymentList === "404" && (
                <Notification
                  type="info"
                  message="No online school fees payment found."
                  closable={false}
                />
              )}
              {!paymentList && <ActivityLoader width={20} />}
            </div>
          </div>
      </MainContent>
    </PageContainer>
        
        <ConfirmationDialog
            title="Delete records"
            alertDescription={`Please confirm that you want to delete the selected records (${selectedIds.length}). This action is irreversible.`}
            show={showDelete}
            handleClose={() => toggleDelete(false)}
            notes="Deleted records can be re-imported if they still exist in the payment gateway. Previously processed payments will lose their “processed” label after deletion, but they will not be processed again to prevent duplicates."
            handleConfirm={handleDeleteConfirmed}
            buttonLabel="Confirm Delete"
        />
          
          <AlertDateSelector
            show={showDateRange}
            onClose={() => setShowDateRange(false)}
            onConfirm={checkForPayments}
            buttonLabel="Check for Payments"
        />

        <ConfirmationDialog
            title="Mark Reconciled"
            alertDescription={`Please confirm that you want to mark the selected records (${selectedIds.length}) as reconciled.`}
            notes="Make sure the selected record(s) appear in your bank statement before confirming."
            show={showReconciled}
            handleClose={() => toggleReconciled(false)}
            handleConfirm={handleReconciledConfirmed}
            buttonLabel="Confirm"
        />

        <ConfirmationDialog
          title="Mark Unreconciled"
          alertDescription={`Please confirm that you want to mark the selected records (${selectedIds.length}) as unreconciled.`}
          notes={
            <>
              Make sure the selected record(s) <strong>do not</strong> appear in your bank statement before confirming.
            </>
          }
          show={showUnreconciled}
          handleClose={() => toggleUnreconciled(false)}
          handleConfirm={handleUnreconciledConfirmed}
          buttonLabel="Confirm"
        />

        <ConfirmationDialog
            title="Process Payments"
            alertDescription={processType === "selection" ? `Please confirm that you want to process the selected records (${selectedIds.length}). Outstanding students fees balances will be settled accordingly.` : "Please confirm that you want to process all pending online payments. This will process a maximum of 100 records. Outstanding students fees balances will be settled accordingly."}
            show={showProcessConfirm}
            handleClose={() => toggleProcessConfirm(false)}
            handleConfirm={processType === "selection" ? confirmProcessSelection : handleProcessPayments}
            buttonLabel="Confirm"
        />

        <ConfirmationDialog
            title="Reconcile Payments"
            alertDescription={`Please confirm that you want to attempt reconciliation for the selected records (${selectedIds.length}). Make sure the selection is processed before attempting reconciliation.`}
            notes={`This will attempt to reconcile the selected payments with your bank statement. Records that can be reconciled will be marked as reconciled, while those that cannot will be marked as failed reconciliation.`}
            show={showReconcile}
            handleClose={() => toggleReconcile(false)}
            handleConfirm={confirmReconcileSelection}
            buttonLabel="Reconcile Now"
        />

    </>

  );
};

export default OnlineSchoolPaymentPage;
